/**
 * SchemaDetailsDialog - Full-screen dialog for viewing/editing schema details
 *
 * Displays complete schema information including fields, metadata, and usage.
 * Supports editing mode with field modifications and version creation.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Edit, Save, XCircle, Loader2, AlertTriangle, Database } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/ui/data-table';
import { useSchema, useCreateSchemaVersion, useUpdateSchema } from '@/features/schemas/hooks/useSchemas';
import { CompatibilityBadge } from './CompatibilityBadge';
import { useToast } from '@/hooks/useToast';
import { validateFields, validateSchemaName } from '@/features/schemas/utils/schemaValidation';
import type { SchemaField, FieldType } from '@/features/schemas/types/schema.types';

interface SchemaDetailsDialogProps {
  /**
   * Schema ID to display
   */
  schemaId: string | null;
  /**
   * Callback when dialog is closed
   */
  onClose: () => void;
}

/**
 * Available field types for dropdown
 */
const FIELD_TYPES: FieldType[] = [
  'string',
  'integer',
  'bigint',
  'float',
  'double',
  'boolean',
  'timestamp',
  'date',
  'binary',
  'array',
  'map',
  'struct',
];


/**
 * SchemaDetailsDialog Component
 *
 * Full-screen dialog showing detailed schema information.
 * Allows editing fields and creating new schema versions.
 *
 * @example
 * ```tsx
 * const [selectedId, setSelectedId] = useState<string | null>(null);
 *
 * <SchemaDetailsDialog
 *   schemaId={selectedId}
 *   onClose={() => setSelectedId(null)}
 * />
 * ```
 */
export const SchemaDetailsDialog: React.FC<SchemaDetailsDialogProps> = ({
  schemaId,
  onClose,
}) => {
  const { toast } = useToast();

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState<SchemaField[]>([]);
  const [editedName, setEditedName] = useState('');

  // Fetch schema details
  const { data: schema, isLoading, error } = useSchema(schemaId || '', { enabled: !!schemaId });
  const createVersion = useCreateSchemaVersion();
  const updateSchema = useUpdateSchema();

  /**
   * Initialize edited fields and name when schema loads or edit mode is toggled.
   *
   * This mirrors [schema, isEditMode] the same way the effect it replaces did,
   * but adjusts state during render instead of in a post-commit effect so the
   * edit buffers are ready in the same paint (React "adjusting state when a
   * prop changes" pattern: https://react.dev/reference/react/useState#storing-information-from-previous-renders).
   * `schema` comes from react-query with default structural sharing, so its
   * reference only changes when the underlying data actually changes -- safe
   * to compare by identity here (an inline literal would never converge).
   */
  const [lastInitializedFor, setLastInitializedFor] = useState<{
    schema: typeof schema;
    isEditMode: boolean;
  } | null>(null);

  if (
    schema &&
    isEditMode &&
    (!lastInitializedFor ||
      lastInitializedFor.schema !== schema ||
      lastInitializedFor.isEditMode !== isEditMode)
  ) {
    setLastInitializedFor({ schema, isEditMode });
    setEditedFields([...schema.fields]);
    setEditedName(schema.name);
  }

  /**
   * Validate edited fields and name
   */
  const fieldValidationErrors = useMemo(
    () => (isEditMode ? validateFields(editedFields) : []),
    [editedFields, isEditMode]
  );

  const nameValidationErrors = useMemo(
    () => (isEditMode ? validateSchemaName(editedName) : []),
    [editedName, isEditMode]
  );

  const validationErrors = useMemo(
    () => [...fieldValidationErrors, ...nameValidationErrors],
    [fieldValidationErrors, nameValidationErrors]
  );

  /**
   * Update field in edited fields array
   */
  const updateField = useCallback(
    (index: number, updates: Partial<SchemaField>) => {
      setEditedFields((prev) => {
        const newFields = [...prev];
        newFields[index] = { ...newFields[index], ...updates };
        return newFields;
      });
    },
    []
  );

  /**
   * Column definitions. Renders Input/Select controls in edit mode; static
   * text in view mode. updateField writes back to local state on change.
   */
  const columns = useMemo<ColumnDef<SchemaField>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Field Name',
        cell: ({ row }) =>
          isEditMode ? (
            <Input
              value={row.original.name ?? ''}
              onChange={(e) => updateField(row.index, { name: e.target.value })}
              className="h-8"
            />
          ) : (
            <span className="font-medium">{row.original.name}</span>
          ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) =>
          isEditMode ? (
            <Select
              value={row.original.type ?? ''}
              onValueChange={(value) => updateField(row.index, { type: value as FieldType })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            row.original.type
          ),
      },
      {
        accessorKey: 'nullable',
        header: 'Nullable',
        cell: ({ row }) =>
          isEditMode ? (
            <Select
              value={row.original.nullable ? 'Yes' : 'No'}
              onValueChange={(value) => updateField(row.index, { nullable: value === 'Yes' })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          ) : row.original.nullable ? (
            'Yes'
          ) : (
            'No'
          ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) =>
          isEditMode ? (
            <Input
              value={row.original.description ?? ''}
              onChange={(e) => updateField(row.index, { description: e.target.value })}
              className="h-8"
            />
          ) : (
            <span className="text-muted-foreground">{row.original.description ?? ''}</span>
          ),
      },
      {
        accessorKey: 'isPII',
        header: 'PII',
        cell: ({ row }) =>
          (row.original as any).isPII ? (
            <Badge variant="destructive" className="ml-2">
              PII
            </Badge>
          ) : null,
      },
    ],
    [isEditMode, updateField]
  );

  /**
   * Handle toggle edit mode
   */
  const handleToggleEdit = useCallback(() => {
    if (isEditMode) {
      // Cancel editing
      setIsEditMode(false);
      setEditedFields([]);
      setEditedName('');
    } else {
      // Enter edit mode
      setIsEditMode(true);
      if (schema) {
        setEditedFields([...schema.fields]);
        setEditedName(schema.name);
      }
    }
  }, [isEditMode, schema]);

  /**
   * Handle save changes
   */
  const handleSave = useCallback(async () => {
    if (!schema || validationErrors.length > 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fix all validation errors before saving',
        variant: 'destructive',
      });
      return;
    }

    try {
      const nameChanged = editedName !== schema.name;
      const fieldsChanged = JSON.stringify(editedFields) !== JSON.stringify(schema.fields);

      // Update schema name if changed
      if (nameChanged) {
        await updateSchema.mutateAsync({
          id: schema.id,
          data: {
            name: editedName,
            compatibility_mode: schema.compatibility_mode,
            fields: schema.fields, // Backend requires fields even for name-only updates
          },
        });
      }

      // Create new version if fields changed
      if (fieldsChanged) {
        await createVersion.mutateAsync({
          id: schema.id,
          data: {
            fields: editedFields,
            compatibility_mode: schema.compatibility_mode,
          },
        });
      }

      const updateMessage = nameChanged && fieldsChanged
        ? `Schema renamed to "${editedName}" and new version created`
        : nameChanged
        ? `Schema renamed to "${editedName}"`
        : `New version of schema "${schema.name}" has been created`;

      toast({
        title: 'Schema Updated',
        description: updateMessage,
      });

      setIsEditMode(false);
      setEditedFields([]);
      setEditedName('');
    } catch (error: any) {
      console.error('Failed to update schema:', error);

      toast({
        title: 'Update Failed',
        description:
          error.response?.data?.detail ||
          'Failed to update schema. Please check your changes and try again.',
        variant: 'destructive',
      });
    }
  }, [schema, editedFields, editedName, validationErrors, createVersion, updateSchema, toast]);

  if (!schemaId) return null;

  const displayFields = isEditMode ? editedFields : schema?.fields || [];

  return (
    <Dialog open={!!schemaId} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {isEditMode ? 'Edit Schema' : (schema?.name || 'Loading...')}
          </DialogTitle>
          <DialogDescription>
            {schema?.description || 'Schema details and field definitions'}
          </DialogDescription>
          {isEditMode && (
            <div className="space-y-2 pt-4">
              <Label htmlFor="schema-name" className="text-base font-semibold">
                Schema Name
              </Label>
              <Input
                id="schema-name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className={nameValidationErrors.length > 0 ? 'border-destructive' : ''}
                placeholder="Enter schema name"
              />
              {nameValidationErrors.length > 0 && (
                <div className="space-y-1">
                  {nameValidationErrors.map((error, index) => (
                    <p key={index} className="text-sm text-destructive">
                      {error.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load schema: {error.message || 'Unknown error'}
            </AlertDescription>
          </Alert>
        )}

        {/* Schema Details */}
        {schema && (
          <div className="flex-1 overflow-y-auto space-y-6">
            {/* Metadata Card */}
            <Card>
              <CardHeader>
                <CardTitle>Schema Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Compatibility Mode</p>
                    <CompatibilityBadge mode={schema.compatibility_mode} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Field Count</p>
                    <p className="font-medium">{schema.fields.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Version</p>
                    <p className="font-medium">{schema.version || 1}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Usage Count</p>
                    <p className="font-medium">{schema.usageCount || 0} datasets</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {format(new Date(schema.created_at), 'MMM d, yyyy HH:mm')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Created By</p>
                    <p className="font-medium">{schema.created_by || 'Unknown'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fields Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Schema Fields</CardTitle>
                    <CardDescription>
                      {isEditMode
                        ? 'Edit schema name above and click on cells to edit field properties'
                        : 'Field definitions and data types'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {isEditMode ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleToggleEdit}
                          disabled={createVersion.isPending || updateSchema.isPending}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSave}
                          disabled={validationErrors.length > 0 || createVersion.isPending || updateSchema.isPending}
                        >
                          {(createVersion.isPending || updateSchema.isPending) ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" size="sm" onClick={handleToggleEdit}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Schema
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Validation Errors:</strong>
                      <ul className="list-disc list-inside mt-2">
                        {validationErrors.map((error, index) => (
                          <li key={index}>{error.message}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Fields DataTable */}
                <DataTable
                  data={displayFields}
                  columns={columns}
                  pageSize={50}
                />
              </CardContent>
            </Card>

            {/* Linked Datasets */}
            <Card>
              <CardHeader>
                <CardTitle>Linked Datasets</CardTitle>
                <CardDescription>
                  Datasets currently linked to this schema
                </CardDescription>
              </CardHeader>
              <CardContent>
                {schema.linked_datasets && schema.linked_datasets.length > 0 ? (
                  <div className="space-y-2">
                    {schema.linked_datasets.map((ds) => (
                      <div
                        key={ds.dataset_id}
                        className="flex items-center justify-between p-2 border rounded"
                      >
                        <div className="flex items-center gap-2">
                          <Database className="h-4 w-4 text-muted-foreground" />
                          {/* react-router Link, not a bare <a href>: a full
                              page load drops the in-memory JWT and logs the
                              operator out (#1029). */}
                          <Link
                            to={`/datasets/${ds.dataset_id}`}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {ds.dataset_name}
                          </Link>
                        </div>
                        {ds.linked_at && (
                          <span className="text-sm text-muted-foreground">
                            Linked {format(new Date(ds.linked_at), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No datasets linked to this schema
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
